import { v4 as uuidv4 } from 'uuid';

import { getSavedSearchExists } from '../../api/saved-search';
import { DISTRIBUTION_TABLE_VALIDATION_PROPS } from '../../constants/SmartInsightConstants';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { translate } from '../../form-designer/utils/form-manager.utils';
import { actionsTableValidationProps } from '../components/ActionsTable';
import { columnsTableValidationProps } from '../components/ColumnsTable';
import { searchCriteriaTableValidationProps } from '../components/SearchCriteriaTable';
import { sortTableValidationProps } from '../components/SortTable';
import {
  COLUMNS_IS_NAVIGATION_COLUMN,
  COLUMNS_SHOW_IN_GRID,
  MAX_SAVED_SEARCH_NAME_LENGTH,
  SEARCH_NAME_VALIDATION_ERROR_MESSAGES,
} from '../constants/editor.constants';

export const isFieldPropUnique = ({ items = [], prop, value }) => {
  const itemsByProp = items.filter((item) => item[prop]?.toUpperCase() === value?.toUpperCase());
  return itemsByProp.length === 1;
};

export const addError = ({
  validationErrors = [],
  translations = {},
  prop,
  instanceId = 'savedSearch',
  message = '',
}) => {
  validationErrors.push({
    errorId: uuidv4(),
    instanceId,
    controlInfo: {
      prop,
    },
    message: `${translate(translations, message)}.`,
    source: WARNING_SOURCES.VALIDATION,
  });
};

export const isValidName = (name = '') => {
  return RegExp('^(a-z|A-Z|0-9)*[^\\/:*?""<>,|\'%]*$').test(name);
};

export const validateSavedSearchName = async ({
  validationErrors = [],
  savedSearch = {},
  originalSavedSearch = {},
  translations = {},
}) => {
  const prop = 'searchName';

  if (!savedSearch.searchName) {
    addError({
      validationErrors,
      translations,
      prop,
      message: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.REQUIRED,
    });

    return validationErrors;
  }

  if (savedSearch.searchName.length > MAX_SAVED_SEARCH_NAME_LENGTH) {
    addError({
      validationErrors,
      translations,
      prop,
      message: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.MAX_LENGTH,
    });

    return validationErrors;
  }

  if (!isValidName(savedSearch.searchName)) {
    addError({
      validationErrors,
      translations,
      prop,
      message: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    });

    return validationErrors;
  }

  const isNameChanged = savedSearch.searchName !== originalSavedSearch.searchName;

  if (!isNameChanged) {
    return;
  }

  const savedSearchExistsResponse = await getSavedSearchExists({
    name: savedSearch.searchName,
    entityName: savedSearch.entityName,
  });

  if (savedSearchExistsResponse.exists) {
    addError({
      validationErrors,
      translations,
      prop,
      message: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.UNIQUE,
    });
  }
};

export const validateRequired = ({
  item,
  validationDetails = {},
  validationErrors = [],
  translations = {},
}) => {
  if (validationDetails.required && !item[validationDetails?.prop]) {
    addError({
      validationErrors,
      translations,
      instanceId: item.rowId,
      prop: validationDetails?.prop,
      message: `${validationDetails.errorLabel} in ${validationDetails.tableName} is required`,
    });
  }
};

export const validateUnique = ({
  items = [],
  item,
  validationDetails = {},
  validationErrors = [],
  translations = {},
}) => {
  if (
    validationDetails.unique &&
    item[validationDetails?.prop] &&
    !isFieldPropUnique({
      items,
      prop: validationDetails?.prop,
      value: item[validationDetails?.prop],
    })
  ) {
    addError({
      validationErrors,
      translations,
      instanceId: item.rowId,
      prop: validationDetails?.prop,
      message: `${validationDetails.errorLabel} in ${validationDetails.tableName} should be unique`,
    });
  }
};

export const validateControls = ({
  items = [],
  controlsValidationProps = [],
  validationErrors = [],
  translations = {},
}) => {
  items.forEach((item) => {
    controlsValidationProps.forEach((validationDetails) => {
      validateRequired({ item, validationDetails, validationErrors, translations });
      validateUnique({ items, item, validationDetails, validationErrors, translations });
    });
  });
};

export const validateColumns = ({
  validationErrors,
  columns = [],
  translations,
  isDocRepository,
}) => {
  validateControls({
    items: columns,
    controlsValidationProps: columnsTableValidationProps,
    validationErrors,
    translations,
  });

  const columnsValid = columns.some((column) => column[COLUMNS_SHOW_IN_GRID]);

  if (!columnsValid && !isDocRepository) {
    addError({
      validationErrors,
      translations,
      message: 'At least one column in Columns must be defined with "Show In Grid" as "Yes"',
    });
  }

  const invalidNavigationColumn = columns.some(
    (column) => !column[COLUMNS_SHOW_IN_GRID] && column[COLUMNS_IS_NAVIGATION_COLUMN]
  );

  if (invalidNavigationColumn) {
    addError({
      validationErrors,
      translations,
      message: 'Column cannot be a Navigation Column if "Show In Grid" is set to "No"',
    });
  }
};

export const validateSavedSearchOnSave = async ({
  translations = {},
  validateSearchName = false,
  savedSearches = {},
  isDocRepository = false,
}) => {
  const validationErrors = [];
  const searches = Object.values(savedSearches);

  await Promise.all(
    searches.map(async ({ savedSearch = {}, originalSavedSearch = {} }) => {
      if (validateSearchName) {
        await validateSavedSearchName({
          validationErrors,
          savedSearch,
          originalSavedSearch,
          translations,
        });
      }

      validateColumns({
        validationErrors,
        columns: savedSearch.columns,
        translations,
        isDocRepository,
      });

      validateControls({
        items: savedSearch.searchCriteria,
        controlsValidationProps: searchCriteriaTableValidationProps,
        validationErrors,
        translations,
      });

      validateControls({
        items: savedSearch.sort,
        controlsValidationProps: sortTableValidationProps,
        validationErrors,
        translations,
      });

      validateControls({
        items: savedSearch.actions,
        controlsValidationProps: actionsTableValidationProps,
        validationErrors,
        translations,
      });

      validateControls({
        items: savedSearch.distribution?.distributionList,
        controlsValidationProps: DISTRIBUTION_TABLE_VALIDATION_PROPS,
        validationErrors,
        translations,
      });
    })
  );

  return validationErrors;
};

export const validateSearchNameOnSaveAs = (searchName) => {
  if (!searchName) {
    return {
      hasError: true,
      errorText: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.REQUIRED,
    };
  }

  if (searchName.length > MAX_SAVED_SEARCH_NAME_LENGTH) {
    return {
      hasError: true,
      errorText: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.MAX_LENGTH,
    };
  }

  if (!isValidName(searchName)) {
    return {
      hasError: true,
      errorText: SEARCH_NAME_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    };
  }

  return { hasError: false, errorText: '' };
};
